import React from "react"
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	TextField,
	Typography,
} from "@mui/material"
import {Controller, useForm} from "react-hook-form"
import {zodResolver} from "@hookform/resolvers/zod"

import {useCustomerID} from "../../useCustomerID"

import {offerFormSchema, OfferFormData} from "./offerFormValidation"

import {useCreateCustomerOffer} from "@indebted/api/customerOffersAPI"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type CreateOfferModalProps = {
	onClose: () => void
	onSuccess: () => void
	minInstalmentAmount?: number
}

function CreateOfferModal({onClose, onSuccess}: CreateOfferModalProps) {
	const customerID = useCustomerID()
	const {notification} = useNotification()
	const [agentEmail] = useAgent()
	const [offerID] = React.useState(crypto.randomUUID())

	const {mutateAsync, isPending} = useCreateCustomerOffer()

	const {
		control,
		handleSubmit,
		formState: {errors},
	} = useForm<OfferFormData>({
		resolver: zodResolver(offerFormSchema),
		defaultValues: {
			startDate: "",
			endDate: "",
			notes: "",
			oneOff: {
				discountRate: undefined,
			},
			plan: {
				discountRate: undefined,
				maxTimeToPayWeeks: undefined,
			},
		},
	})

	const limitDecimalPlaces = (value: string, maxDecimals: number): string => {
		const parts = value.split(".")
		if (parts.length === 2 && parts[1].length > maxDecimals) {
			return `${parts[0]}.${parts[1].slice(0, maxDecimals)}`
		}
		return value
	}

	const onSubmit = async (data: OfferFormData) => {
		try {
			const tiers: Array<
				| {tierId: string; tierType: "ONE_OFF"; discountRate: number}
				| {tierId: string; tierType: "PLAN"; discountRate: number; timeFrameWeeks: number}
			> = []

			if (data.oneOff?.discountRate !== undefined) {
				tiers.push({
					tierId: crypto.randomUUID(),
					tierType: "ONE_OFF",
					discountRate: data.oneOff.discountRate / 100,
				})
			}

			if (data.plan?.discountRate !== undefined && data.plan?.maxTimeToPayWeeks !== undefined) {
				tiers.push({
					tierId: crypto.randomUUID(),
					tierType: "PLAN",
					discountRate: data.plan.discountRate / 100,
					timeFrameWeeks: data.plan.maxTimeToPayWeeks,
				})
			}

			await mutateAsync({
				customerID,
				offerId: offerID,
				startDate: data.startDate,
				endDate: data.endDate,
				tiers,
				agentEmail: agentEmail || undefined,
				notes: data.notes || undefined,
			})

			notification.success("Customer offer created successfully")
			onSuccess()
		} catch (error) {
			notification.error((error as {Message?: string}).Message || "Failed to create customer offer")
		}
	}

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Create Customer Offer</DialogTitle>
			<form onSubmit={handleSubmit(onSubmit)}>
				<DialogContent>
					<Box display="flex" flexDirection="column" style={{gap: "16px"}}>
						<Typography variant="body2" color="textSecondary">
							This offer will be available to the customer in the given time period.
						</Typography>
						<Box display="grid" gridTemplateColumns="1fr 1fr" style={{gap: "16px"}}>
							<Controller
								name="startDate"
								control={control}
								render={({field}) => (
									<TextField
										{...field}
										label="Start Date"
										type="date"
										InputLabelProps={{shrink: true}}
										error={!!errors.startDate}
										helperText={errors.startDate?.message}
										fullWidth
										required
									/>
								)}
							/>
							<Controller
								name="endDate"
								control={control}
								render={({field}) => (
									<TextField
										{...field}
										label="End Date"
										type="date"
										InputLabelProps={{shrink: true}}
										error={!!errors.endDate}
										helperText={errors.endDate?.message}
										fullWidth
										required
									/>
								)}
							/>
						</Box>

						<Box>
							<Typography variant="subtitle1" gutterBottom>
								One-Off Payment
							</Typography>
							<Controller
								name="oneOff.discountRate"
								control={control}
								render={({field: {value, ...field}}) => (
									<TextField
										{...field}
										value={value ?? ""}
										label="Discount"
										type="number"
										inputProps={{step: "0.001", min: "0", max: "99.99"}}
										InputProps={{
											endAdornment: <InputAdornment position="end">%</InputAdornment>,
										}}
										onChange={(e) => {
											const limited = limitDecimalPlaces(e.target.value, 3)
											field.onChange(limited ? parseFloat(limited) : undefined)
										}}
										error={!!errors.oneOff?.discountRate}
										helperText={errors.oneOff?.discountRate?.message}
										fullWidth
									/>
								)}
							/>
						</Box>

						<Box>
							<Typography variant="subtitle1" gutterBottom>
								Payment Plan
							</Typography>
							<Box display="grid" gridTemplateColumns="1fr 1fr" style={{gap: "16px"}}>
								<Controller
									name="plan.discountRate"
									control={control}
									render={({field: {value, ...field}}) => (
										<TextField
											{...field}
											value={value ?? ""}
											label="Discount"
											type="number"
											inputProps={{step: "0.001", min: "0", max: "99.99"}}
											InputProps={{
												endAdornment: <InputAdornment position="end">%</InputAdornment>,
											}}
											onChange={(e) => {
												const limited = limitDecimalPlaces(e.target.value, 3)
												field.onChange(limited ? parseFloat(limited) : undefined)
											}}
											error={!!errors.plan?.discountRate}
											helperText={errors.plan?.discountRate?.message}
											fullWidth
										/>
									)}
								/>
								<Controller
									name="plan.maxTimeToPayWeeks"
									control={control}
									render={({field: {value, ...field}}) => (
										<TextField
											{...field}
											value={value ?? ""}
											label="Maximum Time to Pay (weeks)"
											type="number"
											inputProps={{min: "1"}}
											onChange={(e) => {
												field.onChange(e.target.value ? parseInt(e.target.value) : undefined)
											}}
											error={!!errors.plan?.maxTimeToPayWeeks}
											helperText={errors.plan?.maxTimeToPayWeeks?.message}
											fullWidth
										/>
									)}
								/>
							</Box>
						</Box>

						<Controller
							name="notes"
							control={control}
							render={({field}) => (
								<TextField {...field} label="Notes (optional)" multiline minRows={3} fullWidth />
							)}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} disabled={isPending} type="button">
						Cancel
					</Button>
					<Button type="submit" color="primary" variant="contained" disabled={isPending}>
						{isPending ? <CircularProgress size={24} /> : "Create Offer"}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	)
}

export {CreateOfferModal}
